/**
 * @fileoverview AR-924 — Drawer shell of the teacher deliverable view: fixed header, scrollable body, footer.
 * Every way out (X, backdrop, Escape) goes through `onRequestClose`, so the owner's guard is the single judge.
 */

import type { KeyboardEvent, ReactNode } from 'react'
import { ActionIcon, Box, Group, Stack, Text, Title } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import LumiarDrawer from 'components/LumiarDrawer/LumiarDrawer'

/** Figma: 600px panel, 32px inset. */
const TEACHER_DRAWER_WIDTH = 'min(600px, 100vw)'
const CLOSE_BUTTON_SIZE = 36
const CLOSE_ICON_SIZE = 18
/** Figma: 8px between the title and "Student: …". */
const TITLE_SUBTITLE_GAP = 8

interface TeacherDrawerShellProps {
  readonly opened: boolean
  readonly title: string
  readonly subtitle: string
  readonly onRequestClose: () => void
  readonly footer?: ReactNode
  readonly children: ReactNode
}

export function TeacherDrawerShell(props: TeacherDrawerShellProps) {
  const { opened, title, subtitle, onRequestClose, footer, children } = props
  const { t } = useTranslation()

  function handleVisibilityChange(visible: boolean) {
    if (!visible) onRequestClose()
  }

  // Escape is handled here, not by Rodal: nested Mantine overlays (portals) never bubble it to the drawer.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return
    if (!event.currentTarget.contains(event.target as Node)) return
    event.preventDefault()
    onRequestClose()
  }

  return (
    <LumiarDrawer visibility={opened} onChange={handleVisibilityChange} side="right" scrollable={false}>
      <Box
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        sx={{ width: TEACHER_DRAWER_WIDTH, height: '100vh', display: 'flex', flexDirection: 'column', outline: 'none' }}
      >
        <Group position="apart" align="flex-start" px="lg" pt="lg" pb="lg" noWrap>
          <Stack spacing={TITLE_SUBTITLE_GAP}>
            <Title order={2}>{title}</Title>
            <Text size="sm" color="dimmed">{subtitle}</Text>
          </Stack>
          <ActionIcon
            variant="default"
            radius="xl"
            size={CLOSE_BUTTON_SIZE}
            onClick={onRequestClose}
            aria-label={t('task_tracker.teacher_upload.close')}
          >
            <IconX size={CLOSE_ICON_SIZE} />
          </ActionIcon>
        </Group>

        <Box px="lg" pb="lg" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {children}
        </Box>

        {footer && (
          <Box
            px="lg"
            py="md"
            sx={theme => ({ borderTop: `1px solid ${theme.colors.gray[2]}`, background: theme.white, flexShrink: 0 })}
          >
            {footer}
          </Box>
        )}
      </Box>
    </LumiarDrawer>
  )
}
